import { PORTFOLIO_PAGE_SIZE } from '@repo/api-contracts';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { usePortfolioCategoriesQuery, usePortfoliosQuery } from '@/entities/portfolio';
import { PortfolioCategoryManager } from '@/features/portfolio-categories';
import { PortfolioAdminRow, PortfolioOrderEditor } from '@/features/portfolio-management';
import { Button, ContentCard, EmptyState, FilterSelect, PageHeader } from '@/shared/ui';

export const PortfolioManager = () => {
  const [page, setPage] = useState(1);
  const [categoryId, setCategoryId] = useState('');
  const [tab, setTab] = useState<'list' | 'categories' | 'home'>('list');
  const [visited, setVisited] = useState(['list']);
  const tabs = [
    { id: 'list', label: '사례 목록' },
    { id: 'categories', label: '카테고리 관리' },
    { id: 'home', label: '홈 노출 관리' },
  ] as const;
  const query = usePortfoliosQuery(categoryId ? { page, categoryId } : { page });
  const categories = usePortfolioCategoriesQuery();
  const location = useLocation();
  const state: unknown = location.state;
  const message =
    typeof state === 'object' &&
    state !== null &&
    'message' in state &&
    typeof state.message === 'string'
      ? state.message
      : null;
  const totalPages = Math.ceil((query.data?.totalCount ?? 0) / PORTFOLIO_PAGE_SIZE);
  useEffect(() => {
    if (!query.isPending && !query.isError && query.data?.page === page) {
      const lastPage = Math.max(1, totalPages);
      if (page > lastPage) setPage(lastPage);
    }
  }, [page, query.data?.page, query.isError, query.isPending, totalPages]);
  return (
    <>
      <PageHeader
        title="포트폴리오 관리"
        description="제작 사례, 카테고리와 공개·홈 노출 순서를 관리합니다."
        actions={
          <Link
            className="inline-flex h-10 items-center gap-2 rounded-full bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            to="/portfolio/new"
          >
            <Plus size={16} aria-hidden="true" /> 포트폴리오 등록
          </Link>
        }
      />
      <div className="space-y-6">
        <div
          role="tablist"
          aria-label="포트폴리오 관리 메뉴"
          className="flex flex-wrap gap-1 border-b border-foreground/10"
        >
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`portfolio-tab-${item.id}`}
              aria-controls={`portfolio-panel-${item.id}`}
              aria-selected={tab === item.id}
              tabIndex={tab === item.id ? 0 : -1}
              onKeyDown={(event) => {
                const index = tabs.findIndex((candidate) => candidate.id === item.id);
                const next =
                  event.key === 'ArrowRight'
                    ? (index + 1) % tabs.length
                    : event.key === 'ArrowLeft'
                      ? (index + tabs.length - 1) % tabs.length
                      : event.key === 'Home'
                        ? 0
                        : event.key === 'End'
                          ? tabs.length - 1
                          : -1;
                const selected = tabs[next];
                if (!selected) return;
                event.preventDefault();
                setTab(selected.id);
                setVisited((current) =>
                  current.includes(selected.id) ? current : [...current, selected.id],
                );
                document.getElementById(`portfolio-tab-${selected.id}`)?.focus();
              }}
              className={`border-b-2 px-4 py-3 text-sm transition-colors ${tab === item.id ? 'border-primary font-semibold text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              onClick={() => {
                setTab(item.id);
                setVisited((current) =>
                  current.includes(item.id) ? current : [...current, item.id],
                );
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
        {message ? (
          <p role="status" className="text-sm text-muted-foreground">
            {message}
          </p>
        ) : null}
        {visited.includes('categories') ? (
          <div
            role="tabpanel"
            id="portfolio-panel-categories"
            aria-labelledby="portfolio-tab-categories"
            hidden={tab !== 'categories'}
          >
            <PortfolioCategoryManager />
          </div>
        ) : null}
        {visited.includes('home') ? (
          <div
            role="tabpanel"
            id="portfolio-panel-home"
            aria-labelledby="portfolio-tab-home"
            hidden={tab !== 'home'}
          >
            <PortfolioOrderEditor
              mode="home"
              items={query.data?.items ?? []}
              snapshot={query.data?.homeOrderSnapshot}
              onRefresh={async () => {
                const result = await query.refetch();
                return result.data?.homeOrderSnapshot;
              }}
            />
          </div>
        ) : null}
        <div
          role="tabpanel"
          id="portfolio-panel-list"
          aria-labelledby="portfolio-tab-list"
          hidden={tab !== 'list'}
        >
          <ContentCard className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-sm">
                <label htmlFor="portfolio-category-filter">카테고리 필터</label>
                <FilterSelect
                  id="portfolio-category-filter"
                  value={categoryId}
                  disabled={categories.isPending || categories.isError}
                  onChange={(event) => {
                    setCategoryId(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">전체</option>
                  {categories.data?.items.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </FilterSelect>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span>최신 등록순</span>
                {query.data ? <span>총 {query.data.totalCount}개</span> : null}
              </div>
            </div>
            {categories.isError ? (
              <p role="alert" className="text-sm text-destructive">
                카테고리 필터를 불러오지 못했습니다.{' '}
                <button
                  type="button"
                  className="underline"
                  onClick={() => void categories.refetch()}
                >
                  필터 다시 시도
                </button>
              </p>
            ) : null}
            {query.isPending ? (
              <EmptyState>포트폴리오를 불러오는 중…</EmptyState>
            ) : query.isError ? (
              <EmptyState isError>
                <span className="block">포트폴리오를 불러오지 못했습니다.</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => void query.refetch()}
                >
                  다시 시도
                </Button>
              </EmptyState>
            ) : query.data?.items.length ? (
              <ul className="overflow-hidden rounded-xl border border-foreground/[0.06] divide-y divide-foreground/[0.06]">
                {query.data.items.map((item) => (
                  <PortfolioAdminRow
                    key={item.id}
                    item={item}
                    onRefresh={() => void query.refetch()}
                  />
                ))}
              </ul>
            ) : (
              <EmptyState>
                {categoryId
                  ? '해당 카테고리에 등록된 포트폴리오가 없습니다.'
                  : '등록된 포트폴리오가 없습니다.'}
              </EmptyState>
            )}
            {totalPages > 1 ? (
              <div className="flex flex-wrap items-center justify-center gap-4 pt-3">
                <nav aria-label="포트폴리오 페이지" className="flex flex-wrap items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="px-2"
                    aria-label="이전 페이지"
                    disabled={query.isPending || page <= 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    <ChevronLeft aria-hidden="true" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((value) => (
                    <Button
                      key={value}
                      type="button"
                      size="sm"
                      variant={page === value ? 'default' : 'ghost'}
                      aria-label={`${value}페이지`}
                      aria-current={page === value ? 'page' : undefined}
                      disabled={query.isPending}
                      onClick={() => setPage(value)}
                    >
                      {value}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="px-2"
                    aria-label="다음 페이지"
                    disabled={query.isPending || page >= totalPages}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    <ChevronRight aria-hidden="true" />
                  </Button>
                </nav>
                <span className="text-xs text-muted-foreground">
                  {page} / {totalPages}페이지
                </span>
              </div>
            ) : null}
          </ContentCard>
        </div>
      </div>
    </>
  );
};
